import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const CHANTIERS = [
  {
    id: "solar",
    tag: "Panneaux photovoltaïques",
    title: "Maison familiale · Île-de-France",
    body: "Pose des panneaux monocristallins, onduleur hybride et mise en service. Livré à une famille satisfaite.",
    image:
      "https://customer-assets-jai6qajn.emergentagent.net/job_energie-habitat-pro/artifacts/c71uh6dx_Image%20ChatGPT%2030%20sept.%202026%2C%2021_12_03.png",
  },
  {
    id: "ite",
    tag: "Isolation thermique par l'extérieur",
    title: "Rénovation complète façade",
    body: "Façade fissurée & tachée transformée en façade neuve avec enduit minéral et traitement des ponts thermiques.",
    image:
      "https://customer-assets-jai6qajn.emergentagent.net/job_energie-habitat-pro/artifacts/cwm41zjj_Image%20ChatGPT%2030%20sept.%202026%2C%2021_21_12.png",
  },
  {
    id: "hvac",
    tag: "Pompe à chaleur",
    title: "Remplacement fioul par PAC air/eau",
    body: "Dépose de la chaudière fioul, pose PAC extérieure + module hydraulique intérieur, gainage soigné.",
    image:
      "https://customer-assets-jai6qajn.emergentagent.net/job_energie-habitat-pro/artifacts/bfsdkjjl_Image%20ChatGPT%2030%20sept.%202026%2C%2021_47_10.png",
  },
];

export default function ChantiersShowcase() {
  const scrollToSolutions = () =>
    document.getElementById("solutions")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="chantiers"
      className="py-20 lg:py-32 bg-[#FDFBF7] border-t border-[#e2d9ce]"
      data-testid="chantiers-showcase"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-6 mb-14 lg:mb-20">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="eyebrow mb-4"
            >
              Nos chantiers · Avant / Après
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-serif-display font-medium text-[36px] sm:text-[52px] lg:text-[68px] leading-[0.98] tracking-tight text-[#0F172A] balance"
            >
              Des <em className="not-italic text-[#2D7A4D]">transformations</em> réelles, pas des promesses.
            </motion.h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 lg:pt-4">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="text-[15px] lg:text-[16px] leading-relaxed text-[#334155]"
            >
              Chaque photo est un chantier HMCD récent, livré par nos équipes
              certifiées. Faites défiler pour voir l'avant/après sur nos trois
              métiers.
            </motion.p>
          </div>
        </div>

        <div className="space-y-8 lg:space-y-14">
          {CHANTIERS.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center"
              data-testid={`chantier-showcase-${c.id}`}
            >
              <div className={`lg:col-span-8 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative rounded-2xl overflow-hidden border border-[#e2d9ce] bg-[#0F172A]">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#0F172A] bg-[#FF9800] px-2.5 py-1 rounded-full shadow-lg">
                    Chantier HMCD · Réel
                  </div>
                </div>
              </div>

              <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#2D7A4D] mb-3">
                  Chantier N°{String(i + 1).padStart(2, "0")} · {c.tag}
                </div>
                <h3 className="font-serif-display text-2xl lg:text-4xl text-[#0F172A] leading-[1.05] tracking-tight">
                  {c.title}
                </h3>
                <p className="mt-4 text-[15px] lg:text-[16px] leading-relaxed text-[#334155]">
                  {c.body}
                </p>
                <button
                  onClick={scrollToSolutions}
                  data-testid={`chantier-cta-${c.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-[14px] text-[#0F172A] font-medium border-b border-[#0F172A] pb-0.5 hover:border-[#2D7A4D] hover:text-[#2D7A4D] transition-colors"
                >
                  Voir cette solution en détail
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
